import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import {
  Box,
  Paper,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  IconButton,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  Fab,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DescriptionIcon,
  Remove as RemoveIcon,
  Print as PrintIcon,
  PictureAsPdf as PdfIcon,
} from '@mui/icons-material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Orcamento, Cliente } from '../../types';
import { apiService } from '../../services/api';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

interface ItemOrcamento {
  id: number;
  descricao: string;
  quantidade: number;
  valorUnitario: number;
  valorTotal: number;
}

interface OrcamentoFormData {
  clienteId: number;
  descricao: string;
  dataOrcamento: string;
  dataVencimento: string;
  status: 'orcamento' | 'pedido' | 'pago' | 'vencido';
  observacoes: string;
  itens: ItemOrcamento[];
}

// Componente otimizado para cada item - usa register diretamente, sem controlled inputs
const ItemOrcamentoRow = React.memo<{
  index: number;
  register: any;
  remove: (index: number) => void;
  getValues: any;
  setValue: any;
  onTotalChange: () => void;
}>(({ index, register, remove, getValues, setValue, onTotalChange }) => {

  const handleCalculateTotal = useCallback(() => {
    const quantidade = parseFloat(getValues(`itens.${index}.quantidade`)) || 0;
    const valorUnitario = parseFloat(getValues(`itens.${index}.valorUnitario`)) || 0;
    const total = quantidade * valorUnitario;
    setValue(`itens.${index}.valorTotal`, total);
    onTotalChange();
  }, [index, getValues, setValue, onTotalChange]);

  const handleRemove = useCallback(() => {
    remove(index);
    // Pequeno delay para garantir que o array foi atualizado
    setTimeout(onTotalChange, 0);
  }, [index, remove, onTotalChange]);

  return (
    <Paper sx={{ p: 2, mb: 2 }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
        <Box sx={{ flex: '1 1 300px', minWidth: 200 }}>
          <TextField
            fullWidth
            label="Descrição"
            {...register(`itens.${index}.descricao`)}
          />
        </Box>
        <Box sx={{ flex: '0 0 100px' }}>
          <TextField
            fullWidth
            label="Qtd"
            type="number"
            {...register(`itens.${index}.quantidade`)}
            onBlur={handleCalculateTotal}
            inputProps={{ min: 0, step: 0.01 }}
          />
        </Box>
        <Box sx={{ flex: '0 0 140px' }}>
          <TextField
            fullWidth
            label="Valor Unit."
            type="number"
            {...register(`itens.${index}.valorUnitario`)}
            onBlur={handleCalculateTotal}
            inputProps={{ min: 0, step: 0.01 }}
            InputProps={{
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            }}
          />
        </Box>
        <Box sx={{ flex: '0 0 140px' }}>
          <TextField
            fullWidth
            label="Total"
            type="number"
            {...register(`itens.${index}.valorTotal`)}
            InputProps={{
              readOnly: true,
              startAdornment: <InputAdornment position="start">R$</InputAdornment>,
            }}
          />
        </Box>
        <Box sx={{ flex: '0 0 auto' }}>
          <IconButton color="error" onClick={handleRemove}>
            <RemoveIcon />
          </IconButton>
        </Box>
      </Box>
    </Paper>
  );
});

ItemOrcamentoRow.displayName = 'ItemOrcamentoRow';

const Orcamentos: React.FC = () => {
  const [orcamentos, setOrcamentos] = useState<Orcamento[]>([]);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [editingOrcamento, setEditingOrcamento] = useState<Orcamento | null>(null);
  const [previewOrcamento, setPreviewOrcamento] = useState<Orcamento | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');
  const [valorTotalGeral, setValorTotalGeral] = useState(0);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // React Hook Form
  const { register, control, handleSubmit, reset, getValues, setValue, formState: { errors }, setError, clearErrors } = useForm<OrcamentoFormData>({
    defaultValues: {
      clienteId: 0,
      descricao: '',
      dataOrcamento: new Date().toISOString().split('T')[0],
      dataVencimento: '',
      status: 'orcamento',
      observacoes: '',
      itens: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'itens',
  });

  const showMessage = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [orcamentosData, clientesData] = await Promise.all([
        apiService.getOrcamentos(),
        apiService.getClientes(),
      ]);
      setOrcamentos(orcamentosData);
      setClientes(clientesData);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalGeral = useCallback(() => {
    const itens = getValues('itens');
    const total = itens.reduce((sum, item) => sum + (parseFloat(String(item.valorTotal)) || 0), 0);
    setValorTotalGeral(total);
  }, [getValues]);

  const handleOpenDialog = async (orcamento?: Orcamento) => {
    if (orcamento) {
      try {
        const orcamentoCompleto = await apiService.getOrcamentoById(orcamento.id);
        setEditingOrcamento(orcamentoCompleto);
        reset({
          clienteId: orcamentoCompleto.clienteId,
          descricao: orcamentoCompleto.descricao,
          dataOrcamento: orcamentoCompleto.dataOrcamento,
          dataVencimento: orcamentoCompleto.dataVencimento,
          status: orcamentoCompleto.status,
          observacoes: orcamentoCompleto.observacoes || '',
          itens: orcamentoCompleto.itens || [],
        });
        const total = (orcamentoCompleto.itens || []).reduce((sum, item) => sum + item.valorTotal, 0);
        setValorTotalGeral(total);
      } catch (error) {
        console.error('Erro ao carregar orçamento:', error);
        setEditingOrcamento(orcamento);
        reset({
          clienteId: orcamento.clienteId,
          descricao: orcamento.descricao,
          dataOrcamento: orcamento.dataOrcamento,
          dataVencimento: orcamento.dataVencimento,
          status: orcamento.status,
          observacoes: orcamento.observacoes || '',
          itens: orcamento.itens || [],
        });
        const total = (orcamento.itens || []).reduce((sum, item) => sum + item.valorTotal, 0);
        setValorTotalGeral(total);
      }
    } else {
      setEditingOrcamento(null);
      reset({
        clienteId: 0,
        descricao: '',
        dataOrcamento: new Date().toISOString().split('T')[0],
        dataVencimento: '',
        status: 'orcamento',
        observacoes: '',
        itens: [],
      });
      setValorTotalGeral(0);
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingOrcamento(null);
  };

  const handleAddItem = useCallback(() => {
    append({
      id: Date.now(),
      descricao: '',
      quantidade: 1,
      valorUnitario: 0,
      valorTotal: 0,
    });
  }, [append]);

  const onSubmit = async (data: OrcamentoFormData) => {
    // Limpar erros anteriores
    clearErrors();

    // Validações frontend
    let hasErrors = false;

    if (!data.clienteId || data.clienteId === 0) {
      setError('clienteId', { type: 'required', message: 'Cliente é obrigatório' });
      hasErrors = true;
    }
    if (!data.descricao || data.descricao.trim() === '') {
      setError('descricao', { type: 'required', message: 'Descrição é obrigatória' });
      hasErrors = true;
    }
    if (!data.dataOrcamento) {
      setError('dataOrcamento', { type: 'required', message: 'Data do orçamento é obrigatória' });
      hasErrors = true;
    }
    if (!data.dataVencimento) {
      setError('dataVencimento', { type: 'required', message: 'Data de vencimento é obrigatória' });
      hasErrors = true;
    }

    if (hasErrors) {
      return;
    }

    try {
      const orcamentoData = {
        ...data,
        valorTotal: valorTotalGeral,
      };

      if (editingOrcamento) {
        await apiService.updateOrcamento(editingOrcamento.id, orcamentoData);
        showMessage('Orçamento atualizado com sucesso!', 'success');
      } else {
        await apiService.createOrcamento(orcamentoData);
        showMessage('Orçamento criado com sucesso!', 'success');
      }
      handleCloseDialog();
      loadData();
    } catch (error: any) {
      console.error('Erro ao salvar orçamento:', error);

      // Tratar erros do backend por campo
      if (error?.fieldErrors) {
        const fieldMapping: Record<string, keyof OrcamentoFormData> = {
          'cliente_id': 'clienteId',
          'data_orcamento': 'dataOrcamento',
          'data_validade': 'dataVencimento',
        };

        Object.entries(error.fieldErrors).forEach(([field, message]) => {
          const mappedField = fieldMapping[field] || field;
          setError(mappedField as keyof OrcamentoFormData, {
            type: 'server',
            message: message as string,
          });
        });
      } else {
        const errorMessage = error?.message || error?.error || 'Erro ao salvar orçamento. Verifique os dados e tente novamente.';
        showMessage(errorMessage, 'error');
      }
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Tem certeza que deseja excluir este orçamento?')) {
      try {
        await apiService.deleteOrcamento(id);
        showMessage('Orçamento excluído com sucesso!', 'success');
        loadData();
      } catch (error: any) {
        console.error('Erro ao excluir orçamento:', error);
        const errorMessage = error?.message || error?.error || 'Erro ao excluir orçamento. Tente novamente.';
        showMessage(errorMessage, 'error');
      }
    }
  };

  const handlePreview = async (orcamento: Orcamento) => {
    try {
      const orcamentoCompleto = await apiService.getOrcamentoById(orcamento.id);
      setPreviewOrcamento(orcamentoCompleto);
    } catch (error) {
      console.error('Erro ao carregar orçamento para preview:', error);
      setPreviewOrcamento(orcamento);
    }
    setPreviewOpen(true);
  };

  const handleGeneratePDF = async () => {
    if (!previewOrcamento) return;

    const element = document.getElementById('orcamento-preview');
    const tableContainer = element?.querySelector('[data-table-container]') as HTMLElement;

    if (!element) return;

    try {
      const originalMaxHeight = tableContainer?.style.maxHeight;
      const originalOverflow = tableContainer?.style.overflow;

      if (tableContainer) {
        tableContainer.style.maxHeight = 'none';
        tableContainer.style.overflow = 'visible';
      }

      await new Promise(resolve => setTimeout(resolve, 100));

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
      });

      if (tableContainer) {
        tableContainer.style.maxHeight = originalMaxHeight || '';
        tableContainer.style.overflow = originalOverflow || '';
      }

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');

      const imgWidth = 210;
      const pageHeight = 295;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;

      let position = 0;

      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`orcamento-${previewOrcamento.id}.pdf`);
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'orcamento': return 'default';
      case 'pedido': return 'info';
      case 'pago': return 'success';
      case 'vencido': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'orcamento': return 'Orçamento';
      case 'pedido': return 'Pedido';
      case 'pago': return 'Pago';
      case 'vencido': return 'Vencido';
      default: return status;
    }
  };

  const clientesMenuItems = useMemo(() =>
    clientes.map((cliente) => (
      <MenuItem key={cliente.id} value={cliente.id}>
        {cliente.nome}
      </MenuItem>
    )), [clientes]);

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'cliente',
      headerName: 'Cliente',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.row.cliente?.nome || 'N/A',
    },
    { field: 'descricao', headerName: 'Descrição', flex: 1, minWidth: 200 },
    {
      field: 'valorTotal',
      headerName: 'Valor',
      width: 120,
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'dataVencimento',
      headerName: 'Vencimento',
      width: 120,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      renderCell: (params) => (
        <Chip
          label={getStatusLabel(params.value)}
          color={getStatusColor(params.value) as any}
          size="small"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Ações',
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            size="small"
            onClick={() => handlePreview(params.row)}
            color="info"
          >
            <PrintIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleOpenDialog(params.row)}
            color="primary"
          >
            <EditIcon />
          </IconButton>
          <IconButton
            size="small"
            onClick={() => handleDelete(params.row.id)}
            color="error"
          >
            <DeleteIcon />
          </IconButton>
        </Box>
      ),
    },
  ];

  const OrcamentoCard: React.FC<{ orcamento: Orcamento }> = ({ orcamento }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <DescriptionIcon sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6" component="div">
              #{orcamento.id} - {orcamento.cliente?.nome}
            </Typography>
          </Box>
          <Chip
            label={getStatusLabel(orcamento.status)}
            color={getStatusColor(orcamento.status) as any}
            size="small"
          />
        </Box>
        <Typography variant="body1" gutterBottom>
          {orcamento.descricao}
        </Typography>
        <Typography variant="h6" color="primary" gutterBottom>
          {formatCurrency(orcamento.valorTotal)}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Vencimento: {formatDate(orcamento.dataVencimento)}
        </Typography>
      </CardContent>
      <CardActions sx={{ justifyContent: 'flex-end' }}>
        <Button
          size="small"
          startIcon={<PrintIcon />}
          onClick={() => handlePreview(orcamento)}
        >
          Visualizar
        </Button>
        <Button
          size="small"
          startIcon={<EditIcon />}
          onClick={() => handleOpenDialog(orcamento)}
        >
          Editar
        </Button>
        <Button
          size="small"
          startIcon={<DeleteIcon />}
          color="error"
          onClick={() => handleDelete(orcamento.id)}
        >
          Excluir
        </Button>
      </CardActions>
    </Card>
  );

  return (
    <Box sx={{ p: { xs: 1, sm: 2, md: 3 } }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3,
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Typography
          variant="h4"
          component="h1"
          sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}
        >
          Orçamentos
        </Typography>
        {!isMobile && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Novo Orçamento
          </Button>
        )}
      </Box>

      {isMobile ? (
        <>
          <Box sx={{ mb: 2 }}>
            {loading ? (
              <Typography>Carregando...</Typography>
            ) : (
              orcamentos.map((orcamento) => (
                <OrcamentoCard key={orcamento.id} orcamento={orcamento} />
              ))
            )}
          </Box>
          <Fab
            color="primary"
            aria-label="add"
            sx={{ position: 'fixed', bottom: 16, right: 16 }}
            onClick={() => handleOpenDialog()}
          >
            <AddIcon />
          </Fab>
        </>
      ) : (
        <Paper sx={{ height: 600, width: '100%' }}>
          <DataGrid
            rows={orcamentos}
            columns={columns}
            loading={loading}
            pageSizeOptions={[5, 10, 25]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 10 },
              },
            }}
            disableRowSelectionOnClick
          />
        </Paper>
      )}

      {/* Dialog para criar/editar orçamento */}
      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogTitle>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6">
                {editingOrcamento ? 'Editar Orçamento' : 'Novo Orçamento'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button variant="contained" type="submit">
                  Salvar
                </Button>
                <Button variant="outlined" onClick={handleCloseDialog}>
                  Cancelar
                </Button>
              </Box>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
                  <Controller
                    name="clienteId"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth error={!!errors.clienteId}>
                        <InputLabel required>Cliente</InputLabel>
                        <Select {...field} label="Cliente *">
                          <MenuItem value={0} disabled>
                            <em>Selecione um cliente</em>
                          </MenuItem>
                          {clientesMenuItems}
                        </Select>
                        {errors.clienteId && (
                          <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                            {errors.clienteId.message}
                          </Typography>
                        )}
                      </FormControl>
                    )}
                  />
                </Box>
                <Box sx={{ flex: '1 1 300px', minWidth: 250 }}>
                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <FormControl fullWidth>
                        <InputLabel>Status</InputLabel>
                        <Select {...field} label="Status">
                          <MenuItem value="orcamento">Orçamento</MenuItem>
                          <MenuItem value="pedido">Pedido</MenuItem>
                          <MenuItem value="pago">Pago</MenuItem>
                          <MenuItem value="vencido">Vencido</MenuItem>
                        </Select>
                      </FormControl>
                    )}
                  />
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Descrição"
                required
                {...register('descricao')}
                error={!!errors.descricao}
                helperText={errors.descricao?.message}
              />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 200px' }}>
                  <TextField
                    fullWidth
                    label="Data do Orçamento"
                    type="date"
                    required
                    {...register('dataOrcamento')}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.dataOrcamento}
                    helperText={errors.dataOrcamento?.message}
                  />
                </Box>
                <Box sx={{ flex: '1 1 200px' }}>
                  <TextField
                    fullWidth
                    label="Data de Vencimento"
                    type="date"
                    required
                    {...register('dataVencimento')}
                    InputLabelProps={{ shrink: true }}
                    error={!!errors.dataVencimento}
                    helperText={errors.dataVencimento?.message}
                  />
                </Box>
              </Box>

              <TextField
                fullWidth
                label="Observações"
                multiline
                rows={2}
                {...register('observacoes')}
              />

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h6">Itens</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AddIcon />}
                    onClick={handleAddItem}
                  >
                    Adicionar Item
                  </Button>
                </Box>
                {fields.map((field, index) => (
                  <ItemOrcamentoRow
                    key={field.id}
                    index={index}
                    register={register}
                    remove={remove}
                    getValues={getValues}
                    setValue={setValue}
                    onTotalChange={calculateTotalGeral}
                  />
                ))}
                <Box sx={{ textAlign: 'right', mt: 2 }}>
                  <Typography variant="h6">
                    Total: {formatCurrency(valorTotalGeral)}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </DialogContent>
        </form>
      </Dialog>

      {/* Dialog para preview/impressão */}
      <Dialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Preview do Orçamento</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<PdfIcon />}
                onClick={handleGeneratePDF}
              >
                Gerar PDF
              </Button>
              <Button
                variant="outlined"
                onClick={() => setPreviewOpen(false)}
              >
                Fechar
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {previewOrcamento && (
            <Box id="orcamento-preview" sx={{ p: 3, bgcolor: 'white', color: 'black' }}>
              <Typography variant="h4" align="center" gutterBottom>
                Brastec Tinturaria
              </Typography>
              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Box sx={{ flex: '1 1 60%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Cliente:
                  </Typography>
                  <Typography sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    {previewOrcamento.cliente?.nome}
                  </Typography>
                  <Typography sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    {previewOrcamento.cliente?.email}
                  </Typography>
                  <Typography sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    {previewOrcamento.cliente?.telefone}
                  </Typography>
                </Box>
                <Box sx={{ flex: '1 1 35%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Orçamento #{previewOrcamento.id}
                  </Typography>
                  <Typography>
                    <strong>Data:</strong> {formatDate(previewOrcamento.dataOrcamento)}
                  </Typography>
                  <Typography>
                    <strong>Vencimento:</strong> {formatDate(previewOrcamento.dataVencimento)}
                  </Typography>
                  <Typography>
                    <strong>Status:</strong> {getStatusLabel(previewOrcamento.status)}
                  </Typography>
                </Box>
              </Box>

              <Box sx={{ mt: 3, mb: 2 }}>
                <Typography component="span" sx={{ fontWeight: 'bold' }}>
                  Descrição:
                </Typography>
                <Typography component="span" sx={{ ml: 1 }}>
                  {previewOrcamento.descricao}
                </Typography>
              </Box>

              <TableContainer
                component={Paper}
                data-table-container
                sx={{
                  mt: 2,
                  maxHeight: '400px',
                  overflow: 'auto',
                  '@media print': {
                    maxHeight: 'none',
                    overflow: 'visible'
                  }
                }}
              >
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{
                          width: '60%',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          backgroundColor: 'white'
                        }}
                      >
                        Descrição
                      </TableCell>
                      <TableCell
                        align="center"
                        sx={{
                          width: '10%',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          backgroundColor: 'white'
                        }}
                      >
                        Qtd
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          width: '15%',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          backgroundColor: 'white'
                        }}
                      >
                        Valor Unit.
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{
                          width: '15%',
                          fontWeight: 'bold',
                          fontSize: '1rem',
                          backgroundColor: 'white'
                        }}
                      >
                        Total
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {previewOrcamento.itens.map((item) => (
                      <TableRow key={item.id}>
                        <TableCell
                          sx={{
                            width: '60%',
                            wordWrap: 'break-word',
                            whiteSpace: 'normal'
                          }}
                        >
                          {item.descricao}
                        </TableCell>
                        <TableCell align="center" sx={{ width: '10%' }}>{item.quantidade}</TableCell>
                        <TableCell align="right" sx={{ width: '15%' }}>{formatCurrency(item.valorUnitario)}</TableCell>
                        <TableCell align="right" sx={{ width: '15%' }}>{formatCurrency(item.valorTotal)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              <Box sx={{ textAlign: 'right', mt: 3 }}>
                <Typography variant="h5">
                  <strong>Total: {formatCurrency(previewOrcamento.valorTotal)}</strong>
                </Typography>
              </Box>

              {previewOrcamento.observacoes && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    Observações:
                  </Typography>
                  <Typography sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                    {previewOrcamento.observacoes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Snackbar para mensagens de feedback */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Orcamentos;
